/**
 * GlassStars - stelle di rating con stile "vetro" ciano (gradiente teal + gloss + glow).
 *
 * Stile unificato in tutto il sito (hero, testimonial clienti, riepilogo metriche,
 * card recensioni Google). Server-compatible: nessun hook, nessuno stato - puo' essere
 * importato sia in server component (es. HomeHeroStatic) sia in client component.
 *
 * - `count`: numero totale di stelle (default 5)
 * - `rating`: quante sono piene (default = count); le restanti sono outline muted
 * - `starClassName`: dimensione/utility per singola stella (default "h-4 w-4")
 * - `className`: classi extra sul wrapper
 *
 * Richiede <GlassStarsDefs /> montato una volta nel root layout (definisce i
 * gradienti referenziati). Gia' presente in app/layout.tsx.
 */

const STAR_PATH =
  "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z";

/**
 * Gradienti condivisi referenziati da tutte le GlassStars (`url(#starGlassFill)` /
 * `url(#starGlassGloss)`). Va renderizzato UNA sola volta nel root layout: cosi'
 * evitiamo ID duplicati e <defs> ripetuti su ogni istanza. Le reference SVG si
 * risolvono a livello di documento, quindi la posizione nel DOM e' indifferente.
 */
export function GlassStarsDefs() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true">
      <defs>
        <linearGradient id="starGlassFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A7F3EA" />
          <stop offset="45%" stopColor="#2FD9C5" />
          <stop offset="100%" stopColor="#0E9C8E" />
        </linearGradient>
        <linearGradient id="starGlassGloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

type GlassStarsProps = {
  count?: number;
  rating?: number;
  starClassName?: string;
  className?: string;
};

export function GlassStars({
  count = 5,
  rating = count,
  starClassName = "h-4 w-4",
  className,
}: GlassStarsProps) {
  return (
    <span
      className={`inline-flex items-center gap-1${className ? ` ${className}` : ""}`}
      role="img"
      aria-label={`${rating} stelle su 5`}
    >
      {Array.from({ length: count }).map((_, index) => {
        const filled = index < rating;
        return (
          <svg
            key={index}
            viewBox="0 0 24 24"
            className={
              filled
                ? `${starClassName} drop-shadow-[0_0_3px_rgba(20,210,190,0.55)]`
                : starClassName
            }
            aria-hidden="true"
          >
            {filled ? (
              <>
                <path
                  d={STAR_PATH}
                  fill="url(#starGlassFill)"
                  stroke="#8FF0E4"
                  strokeWidth="0.7"
                  strokeLinejoin="round"
                />
                <path d={STAR_PATH} fill="url(#starGlassGloss)" />
              </>
            ) : (
              <path
                d={STAR_PATH}
                fill="none"
                stroke="hsl(var(--muted-foreground) / 0.4)"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            )}
          </svg>
        );
      })}
    </span>
  );
}
